import {
  useState,
  useRef,
  useCallback,
  useEffect,
  MouseEvent as ReactMouseEvent,
  PropsWithChildren,
  useMemo,
} from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';

import { measureContentHeight } from './_utils';
import { Notches } from './Notches';
import { SxProps } from '../../types';
import { mergeSx } from '../../utils';
import { Scroller } from '../scroller';

const MIN_HEIGHT_CONSTRAINT = 16;
const MAX_HEIGHT_CONSTRAINT = 1280;

export type ResizableProps = PropsWithChildren<{
  /**
   * Initial height in pixels. Optional when maxHeight is 'max-content',
   * in which case the component will measure and use the content height.
   * Required when maxHeight is a number.
   */
  initialHeight?: number | string;
  /**
   * @default 16
   */
  minHeight?: number;
  /**
   * Maximum height constraint. Can be a number in pixels or 'max-content'
   * to constrain to the actual content height.
   * @default 'max-content'
   */
  maxHeight?: number | 'max-content';
  className?: string;
  sx?: SxProps;
  disabled?: boolean;
  testId?: string;
}>;

const Resizable = ({
  initialHeight,
  minHeight = MIN_HEIGHT_CONSTRAINT,
  maxHeight = 'max-content',
  children,
  className,
  sx,
  disabled,
  testId,
}: ResizableProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const hasInitialMeasurement = useRef(false);

  const startY = useRef(0);
  const startHeight = useRef(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const shouldConstrainToContent = maxHeight === 'max-content';
  if (initialHeight === undefined && !shouldConstrainToContent) {
    console.error(
      'Resizable: initialHeight is required when maxHeight is not "max-content"'
    );
  }

  const _minHeight = useMemo(
    () => Math.max(minHeight, MIN_HEIGHT_CONSTRAINT),
    [minHeight]
  );

  const [renderedHeight, setRenderedHeight] = useState<number | string>(
    initialHeight ?? _minHeight
  );

  // Measure content immediately on mount
  const contentRefCallback = useCallback(
    (content: HTMLDivElement | null) => {
      contentRef.current = content;

      if (
        content &&
        !hasInitialMeasurement.current &&
        shouldConstrainToContent
      ) {
        const measured = measureContentHeight(content);
        setContentHeight(measured);
        if (initialHeight === undefined) {
          setRenderedHeight(measured);
        }
        hasInitialMeasurement.current = true;
      }
    },
    [shouldConstrainToContent, initialHeight]
  );

  const _maxHeight = useMemo(() => {
    let effectiveMaxHeight: number;

    if (shouldConstrainToContent && contentHeight !== null) {
      effectiveMaxHeight = contentHeight;
    } else if (typeof maxHeight === 'number') {
      effectiveMaxHeight = maxHeight;
    } else {
      effectiveMaxHeight = Infinity;
    }

    return Math.max(
      Math.min(effectiveMaxHeight, MAX_HEIGHT_CONSTRAINT),
      _minHeight
    );
  }, [shouldConstrainToContent, contentHeight, maxHeight, _minHeight]);

  const applyHeightConstraints = useCallback(
    (newHeight: number) =>
      Math.min(Math.max(newHeight, _minHeight), _maxHeight),
    [_maxHeight, _minHeight]
  );

  const effectiveHeight =
    typeof renderedHeight === 'number'
      ? applyHeightConstraints(renderedHeight)
      : renderedHeight;

  useEffect(() => {
    if (!shouldConstrainToContent || !contentRef.current) {
      return;
    }

    const handleResize = () => {
      if (!contentRef.current) {
        return;
      }
      const naturalHeight = measureContentHeight(contentRef.current);
      setContentHeight(naturalHeight);
    };

    handleResize();

    resizeObserverRef.current = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserverRef.current.observe(contentRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [shouldConstrainToContent, children]);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      setIsResizing(true);
      startY.current = e.clientY;
      startHeight.current =
        typeof effectiveHeight === 'number'
          ? effectiveHeight
          : contentRef.current?.offsetHeight ?? 0;

      // Prevent text selection during resize
      e.preventDefault();
    },
    [effectiveHeight]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) {
        return;
      }

      const deltaY = e.clientY - startY.current;
      const newHeight = startHeight.current + deltaY;

      setRenderedHeight(applyHeightConstraints(newHeight));
    },
    [isResizing, applyHeightConstraints]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box
      ref={contentRefCallback}
      className={clsx('DS-Resizable', className)}
      sx={{
        position: 'relative',
        maxHeight: _maxHeight,
        minHeight: _minHeight,
        height: effectiveHeight,
      }}
      data-testid={testId}
    >
      <Scroller sx={mergeSx({ height: '100%' }, sx)}>{children}</Scroller>
      {!disabled && _minHeight < _maxHeight && (
        <Notches
          className="DS-Resizable-Handle"
          onMouseDown={handleMouseDown}
          size={24}
          style={{
            position: 'absolute',
            cursor: 'ns-resize',
            padding: 4,
            right: 0,
            bottom: 0,
          }}
          aria-label="resize"
          role="button"
        />
      )}
    </Box>
  );
};

export default Resizable;
